/**
 * useMultiDiseaseFeedback - Hook for submitting multi-disease experiment feedback
 *
 * Handles submission of health priorities and flow path from the modal.
 */

import { useCallback, useState } from 'react';
import { useCreateFeedbackV1ExperimentsFeedbackPost } from '@/api/generated-catch-backend/experiments/experiments';
import type { CreateMultiDiseaseFeedbackRequest } from '@/api/generated-catch-backend/model';

export type FlowPath = 'continue' | 'cancel';

export interface HealthPriorities {
  cancer: boolean;
  heartDisease: boolean;
  dementia: boolean;
  diabetes: boolean;
}

export interface UseMultiDiseaseFeedbackResult {
  /** Submit feedback to the backend */
  submitFeedback: (
    priorities: HealthPriorities,
    flowPath: FlowPath,
    additionalFeedback?: string
  ) => Promise<void>;
  /** Whether feedback is being submitted */
  isSubmitting: boolean;
  /** Whether feedback was successfully submitted */
  isSuccess: boolean;
  /** Error message if submission failed */
  error: string | null;
  /** Reset the state */
  reset: () => void;
}

export function useMultiDiseaseFeedback(): UseMultiDiseaseFeedbackResult {
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const feedbackMutation = useCreateFeedbackV1ExperimentsFeedbackPost();

  const submitFeedback = useCallback(
    async (
      priorities: HealthPriorities,
      flowPath: FlowPath,
      additionalFeedback?: string
    ) => {
      setError(null);
      setIsSuccess(false);

      const request: CreateMultiDiseaseFeedbackRequest = {
        priorityCancer: priorities.cancer,
        priorityHeartDisease: priorities.heartDisease,
        priorityDementia: priorities.dementia,
        priorityDiabetes: priorities.diabetes,
        additionalFeedback: additionalFeedback || null,
        flowPath,
      };

      try {
        await feedbackMutation.mutateAsync({ data: request });
        setIsSuccess(true);
      } catch (err) {
        setError('Failed to submit feedback. Please try again.');
        throw err;
      }
    },
    [feedbackMutation]
  );

  const reset = useCallback(() => {
    setError(null);
    setIsSuccess(false);
  }, []);

  return {
    submitFeedback,
    isSubmitting: feedbackMutation.isPending,
    isSuccess,
    error,
    reset,
  };
}

export default useMultiDiseaseFeedback;
